import React, { useState, useEffect, useRef } from 'react';
import { GZipService } from '../../lib/services/gzipService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { GZipMode } from '../../lib/types/Compression';
import { EncoderToolType } from '../../lib/types/EncodingOperation';
import './GzipCompression.css';

// Local types for the component
type GzipFormat = 'base64' | 'hex' | 'uint8array';
type GzipLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

const GzipFormatValues = {
  Base64: 'base64' as const,
  Hex: 'hex' as const,
  Uint8Array: 'uint8array' as const
};

const GzipLevelValues = {
  None: 0 as const,
  Fastest: 1 as const,
  Fast: 3 as const,
  Default: 6 as const,
  Maximum: 9 as const
};

// Export for JSX template usage
const GzipLevel = GzipLevelValues;
const GzipFormat = GzipFormatValues;

export const GzipCompression: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'compress' | 'decompress'>('compress');
  const [compressionLevel, setCompressionLevel] = useState<GzipLevel>(GzipLevelValues.Default);
  const [outputFormat, setOutputFormat] = useState<GzipFormat>(GzipFormatValues.Base64);
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleProcess = async () => {
    if (!input.trim()) {
      setOutput('');
      setError(null);
      setStats(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setStats(null);

    const processingKey = `${mode}-${compressionLevel}-${outputFormat}-${input.substring(0, 50)}`;
    processingRef.current = processingKey;

    try {
      let result;

      if (mode === 'compress') {
        const config = GZipService.createConfiguration(
          input,
          GZipMode.COMPRESS,
          {
            level: compressionLevel,
            outputFormat,
            // Ensure deterministic output
            timestamp: 0,
            filename: undefined,
            comment: undefined
          }
        );

        result = await GZipService.compress(config);
      } else {
        const config = GZipService.createConfiguration(
          input,
          GZipMode.DECOMPRESS,
          { outputFormat }
        );

        result = await GZipService.decompress(config);
      }

      // Check if this is still the latest operation
      if (processingRef.current === processingKey) {
        setOutput(result.outputData as string);
        setStats(result.metadata);
        setError(null);
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, EncoderToolType.GZIP_COMPRESSION);
        setError(errorMessage);
        setOutput('');
        setStats(null);
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (input) {
      const timer = setTimeout(() => {
        handleProcess();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else {
      setOutput('');
      setError(null);
      setStats(null);
      setIsLoading(false);
    }
  }, [input, mode, compressionLevel, outputFormat]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Result';
    }
  };

  const getButtonClass = () => {
    const baseClass = 'copy-button';
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} success`;
      case 'error':
        return `${baseClass} error`;
      default:
        return baseClass;
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError(null);
    setStats(null);
    setCopyFeedback('idle');
  };

  
  
  const insertSampleText = (type: string) => {
    const samples = {
      lorem: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
      json: '{"name": "John Doe", "age": 30, "city": "New York", "hobbies": ["reading", "coding", "gaming"], "address": {"street": "123 Main St", "city": "New York", "country": "USA"}}',
      xml: '<?xml version="1.0" encoding="UTF-8"?><root><item id="1">First item</item><item id="2">Second item</item><item id="3">Third item</item></root>',
      csv: 'Name,Age,City,Country\nJohn Doe,30,New York,USA\nJane Smith,25,London,UK\nBob Johnson,35,Tokyo,Japan',
      code: 'function fibonacci(n) {\n  if (n <= 1) return n;\n  return fibonacci(n - 1) + fibonacci(n - 2);\n}\n\n// Calculate first 10 numbers\nfor (let i = 0; i < 10; i++) {\n  console.log(fibonacci(i));\n}'
    };

    setInput(samples[type as keyof typeof samples] || '');
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCompressionRatio = () => {
    if (!stats || !stats.originalSize || !stats.compressedSize) return '0%';
    const ratio = ((stats.originalSize - stats.compressedSize) / stats.originalSize) * 100;
    return ratio.toFixed(1) + '%';
  };

  const getSpaceSaved = () => {
    if (!stats || !stats.originalSize || !stats.compressedSize) return '0 Bytes';
    const saved = stats.originalSize - stats.compressedSize;
    return formatBytes(saved);
  };

  return (
    <div className="gzip-compression">
      <h1>GZip Compression/Decompression</h1>
      <p>Compress text data using GZip algorithm to reduce file sizes or decompress GZip data to restore original content</p>

      <div className="encoder-container">
        <div className="form-group">
          <label htmlFor="mode">Operation Mode</label>
          <select
            id="mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as 'compress' | 'decompress')}
            data-testid="mode-select"
          >
            <option value="compress">Compress Text</option>
            <option value="decompress">Decompress GZip</option>
          </select>
        </div>

        {mode === 'compress' && (
          <>
            <div className="form-group">
              <label htmlFor="compression-level">Compression Level</label>
              <select
                id="compression-level"
                value={compressionLevel}
                onChange={(e) => setCompressionLevel(Number(e.target.value) as GzipLevel)}
                data-testid="compression-level-select"
              >
                <option value={GzipLevel.None}>0 (No Compression)</option>
                <option value={GzipLevel.Fastest}>1 (Fastest)</option>
                <option value={GzipLevel.Fast}>3 (Fast)</option>
                <option value={GzipLevel.Default}>6 (Default)</option>
                <option value={GzipLevel.Maximum}>9 (Maximum)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="output-format">Output Format</label>
              <select
                id="output-format"
                value={outputFormat}
                onChange={(e) => setOutputFormat(e.target.value as GzipFormat)}
                data-testid="output-format-select"
              >
                <option value={GzipFormatValues.Base64}>Base64 String</option>
                <option value={GzipFormatValues.Hex}>Hexadecimal</option>
                <option value={GzipFormatValues.Uint8Array}>Uint8 Array</option>
              </select>
            </div>
          </>
        )}

        <div className="form-group">
          <label htmlFor="input-text">
            {mode === 'compress' ? 'Text to Compress' : 'GZip Data to Decompress'}
          </label>
          <textarea
            id="input-text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              mode === 'compress'
                ? 'Enter text to compress...'
                : 'Paste Base64 or Hex GZip data to decompress...'
            }
            rows={8}
            data-testid="input-text"
          />
        </div>

        <div className="sample-section">
          <label>Quick Samples:</label>
          <div className="sample-buttons">
            <button
              onClick={() => insertSampleText('lorem')}
              className="sample-button"
              data-testid="sample-lorem"
            >
              Lorem Ipsum
            </button>
            <button
              onClick={() => insertSampleText('json')}
              className="sample-button"
              data-testid="sample-json"
            >
              JSON Data
            </button>
            <button
              onClick={() => insertSampleText('xml')}
              className="sample-button"
              data-testid="sample-xml"
            >
              XML Document
            </button>
            <button
              onClick={() => insertSampleText('csv')}
              className="sample-button"
              data-testid="sample-csv"
            >
              CSV Data
            </button>
            <button
              onClick={() => insertSampleText('code')}
              className="sample-button"
              data-testid="sample-code"
            >
              Source Code
            </button>
          </div>
        </div>

        {error && (
          <div className="status-error" data-testid="error-message">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="loading-indicator" data-testid="loading-indicator">
            {mode === 'compress' ? 'Compressing...' : 'Decompressing...'}
          </div>
        )}

        {output && (
          <div className="output-section">
            <h3>{mode === 'compress' ? 'Compressed Data' : 'Decompressed Text'}</h3>
            <textarea
              value={output}
              readOnly
              rows={8}
              data-testid="output-text"
            />

            {stats && (
              <div className="compression-stats" data-testid="compression-stats">
                <div className="stat-item">
                  <span className="stat-label">Original Size:</span>
                  <span className="stat-value">{formatBytes(stats.originalSize)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Compressed Size:</span>
                  <span className="stat-value">{formatBytes(stats.compressedSize)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Compression Ratio:</span>
                  <span className="stat-value">{getCompressionRatio()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Space Saved:</span>
                  <span className="stat-value">{getSpaceSaved()}</span>
                </div>
                {stats.compressionTime && (
                  <div className="stat-item">
                    <span className="stat-label">Processing Time:</span>
                    <span className="stat-value">{stats.compressionTime.toFixed(2)}ms</span>
                  </div>
                )}
                              </div>
            )}

            <div className="button-container">
              <button
                ref={copyButtonRef}
                onClick={handleCopy}
                className={getButtonClass()}
                data-testid="copy-button"
              >
                {getButtonText()}
              </button>

              
              <button
                onClick={clearAll}
                className="clear-button"
                data-testid="clear-button"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        <div className="info-section">
          <h3>GZip Reference</h3>
          <div className="reference-grid">
            <div className="reference-item">
              <strong>Compression Levels:</strong> 0-9 (0=none, 9=maximum)
            </div>
            <div className="reference-item">
              <strong>Algorithm:</strong> DEFLATE (RFC 1951)
            </div>
            <div className="reference-item">
              <strong>Max Efficiency:</strong> Text files, JSON, XML, CSV
            </div>
            <div className="reference-item">
              <strong>Binary Support:</strong> Images, executables, archives
            </div>
            <div className="reference-item">
              <strong>Universal:</strong> Cross-platform compatibility
            </div>
            <div className="reference-item">
              <strong>Streaming:</strong> Can process large files incrementally
            </div>
          </div>

          <div className="compression-tips">
            <h4>💡 Compression Tips</h4>
            <ul>
              <li><strong>Level 6 (Default):</strong> Best balance of speed and compression</li>
              <li><strong>Level 9 (Maximum):</strong> Best compression, slower processing</li>
              <li><strong>Level 1 (Fastest):</strong> Fastest processing, lower compression</li>
              <li><strong>Text Content:</strong> Typically achieves 60-80% reduction</li>
              <li><strong>Binary Files:</strong> Usually 5-30% reduction depending on type</li>
              <li><strong>Repeated Data:</strong> Higher compression ratios</li>
              <li><strong>Random Data:</strong> Minimal compression, may increase size</li>
            </ul>
          </div>

          <div className="format-info">
            <h4>📄 Output Formats</h4>
            <div className="format-grid">
              <div className="format-item">
                <strong>Base64:</strong> Safe for text transmission
              </div>
              <div className="format-item">
                <strong>Hex:</strong> Debugging and analysis
              </div>
              <div className="format-item">
                <strong>Uint8Array:</strong> JavaScript Array Buffer
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* No-JS Fallback */}
      <noscript>
        <div className="fallback-form">
          <form method="POST" action="/api/gzip" encType="multipart/form-data" aria-label="GZip Fallback Form">
            <div className="form-group">
              <label htmlFor="fallback-mode">Mode:</label>
              <select name="mode" id="fallback-mode" aria-label="Select compression mode">
                <option value="compress">Compress</option>
                <option value="decompress">Decompress</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="fallback-level">Compression Level:</label>
              <select name="level" id="fallback-level" aria-label="Select compression level">
                <option value="6">6 (Default)</option>
                <option value="1">1 (Fastest)</option>
                <option value="3">3 (Fast)</option>
                <option value="9">9 (Maximum)</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="fallback-format">Output Format:</label>
              <select name="format" id="fallback-format" aria-label="Select output format">
                <option value="base64">Base64</option>
                <option value="hex">Hexadecimal</option>
                <option value="uint8array">Uint8 Array</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="fallback-input">Input:</label>
              <textarea name="input" id="fallback-input" rows={8} required aria-label="Input text" />
            </div>
            <button type="submit" aria-label="Process GZip data">Process</button>
            <p>For command-line GZip processing:</p>
            <pre><code># Compress
gzip -c input.txt &gt; output.gz

# Decompress
gzip -d input.gz &gt; output.txt</code></pre>
          </form>
        </div>
      </noscript>
    </div>
  );
};